import { Typography } from "antd";
import { CHART_INK, CHART_ORDINAL_4, CHART_STATUS, CHART_TRACK } from "./chartTheme";

interface ValueMeterProps {
  label: string;
  /** What has been used so far, in the same unit as `limit`. */
  value: number;
  /** The approved total the value is measured against. */
  limit: number;
  /** A second, always-larger figure marked as a tick on the track (issued vs consumed). */
  marker?: { value: number; label: string };
  format: (n: number) => string;
}

/**
 * One ratio against a limit, which is a meter rather than a chart: a two-bar plot of
 * "used" and "approved" makes the reader do the division themselves.
 *
 * The unfilled track is a lighter step of the fill's own ramp so the state reads across
 * the whole bar, and the fill turns to the critical status colour — with the words to
 * match, never colour alone — once it passes the limit.
 */
export function ValueMeter({ label, value, limit, marker, format }: ValueMeterProps) {
  const over = limit > 0 && value > limit;
  const pct = limit > 0 ? Math.min(100, (value / limit) * 100) : 0;
  const markerPct = marker && limit > 0 ? Math.min(100, (marker.value / limit) * 100) : null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <Typography.Text style={{ fontSize: 12, color: CHART_INK.secondary }}>{label}</Typography.Text>
        <Typography.Text style={{ fontSize: 12 }}>
          <span style={{ fontWeight: 600, color: over ? CHART_STATUS.critical : CHART_INK.primary }}>{format(value)}</span>
          <span style={{ color: CHART_INK.muted }}> of {format(limit)}</span>
        </Typography.Text>
      </div>
      <div style={{ position: "relative", height: 10, background: CHART_TRACK, borderRadius: 5, marginTop: 6 }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: over ? CHART_STATUS.critical : CHART_ORDINAL_4[2],
            borderRadius: 5,
          }}
        />
        {markerPct != null && (
          // A hairline tick, not a second bar: it says "this much left the store" without
          // competing with the quantity the meter is actually about.
          <span
            title={`${marker!.label}: ${format(marker!.value)}`}
            style={{
              position: "absolute",
              left: `calc(${markerPct}% - 1px)`,
              top: -2,
              width: 2,
              height: 14,
              background: CHART_INK.secondary,
              borderRadius: 1,
            }}
          />
        )}
      </div>
      {over && (
        <Typography.Text style={{ fontSize: 11, color: CHART_STATUS.critical }}>
          Over the approved quantity
        </Typography.Text>
      )}
    </div>
  );
}
